import { useEffect, useState } from 'react'
import { EventCalendar } from '@/types/Calendar.ts'
import moment, { Moment } from 'moment-timezone'

interface UseAgendaEventsParams {
    calendarEvents: EventCalendar[]
    selectedDate: Moment
    onGroupedEventsReady: (formattedDate: string) => void
}

interface UseAgendaEventsReturn {
    agendaEvents: EventCalendar[]
    groupedEvents: { [key: string]: EventCalendar[] }
    agendaDate: Date[]
}

export function useAgendaEvents({
    calendarEvents,
    selectedDate,
    onGroupedEventsReady
}: UseAgendaEventsParams): UseAgendaEventsReturn {
    const [agendaEvents, setAgendaEvents] = useState<EventCalendar[]>([])
    const [groupedEvents, setGroupedEvents] = useState<{ [key: string]: EventCalendar[] }>({})

    useEffect(() => {
        const startOfMonth: Moment = moment(selectedDate).startOf('month')
        const endOfMonth: Moment = moment(selectedDate).endOf('month')

        const sortedCalendarEvents: EventCalendar[] = calendarEvents
            .flatMap((event: EventCalendar) => {
                const eventStart: Moment = moment(event.start)
                const eventEnd: Moment = moment(event.end)

                const dailyEvents: EventCalendar[] = []

                for (let date: Moment = moment(eventStart); date.isSameOrBefore(eventEnd, 'day'); date.add(1, 'day')) {
                    dailyEvents.push({
                        ...event,
                        start: date.toDate(),
                        end: moment(
                            moment(date).format('YYYY-MM-DD') + ' ' + eventEnd.format('HH:mm'),
                            'YYYY-MM-DD HH:mm'
                        ).toDate()
                    })
                }

                return dailyEvents
            })
            .filter((event: EventCalendar) => moment(event.start).isBetween(startOfMonth, endOfMonth, 'day', '[]'))

        setAgendaEvents(sortedCalendarEvents)

        setGroupedEvents(
            sortedCalendarEvents.reduce((acc: { [key: string]: EventCalendar[] }, event: EventCalendar) => {
                const date: string = moment(event.start).format('YYYY-MM-DD')
                if (!acc[date]) acc[date] = []
                acc[date].push(event)
                return acc
            }, {})
        )

        onGroupedEventsReady(selectedDate.format('YYYY-MM-DD'))
    }, [calendarEvents])

    const agendaDate: Date[] = agendaEvents.map((event: EventCalendar) => moment(event.start).toDate())

    return { agendaEvents, groupedEvents, agendaDate }
}
